import React from 'react';
import { Button } from 'primereact/button';
import axios from 'axios';
import { authorizeGoogleCalendar } from '../../config/googleCalendarApi';
import DisconnectDialog from './DisconnectDialog';

const GoogleCalendarIntegration = ({
    googleCalendarEnabled,
    setGoogleCalendarEnabled,
    currentUser,
    showToast,
    fetchEvents,
    disconnectDialog,
    setDisconnectDialog
}) => {
    const disconnectGoogleCalendar = async () => {
        try {
            showToast('info', 'Processing', 'Disconnecting from Google Calendar...');

            const response = await axios.post('https://api.frazza.online/calendar/disconnect-google', {
                userId: currentUser.uid
            });

            if (response.data.success) {
                setGoogleCalendarEnabled(false);
                showToast('success', 'Success', 'Disconnected from Google Calendar');
            } else {
                showToast('error', 'Error', response.data.message || 'Failed to disconnect from Google Calendar');
            }
        } catch (error) {
            console.error("Failed to disconnect from Google Calendar:", error);
            showToast('error', 'Error', `Disconnection failed: ${error.response?.data?.error || error.message}`);
        }
    };

    const syncGoogleCalendar = async () => {
        try {
            showToast('info', 'Processing', 'Syncing with Google Calendar...');

            const response = await axios.post('https://api.frazza.online/calendar/sync', {
                userId: currentUser.uid
            });

            if (response.data.success) {
                showToast('success', 'Success', 'Calendar synced successfully');
                fetchEvents(); // Refresh events after sync
            } else {
                throw new Error(response.data.error || "Sync failed");
            }
        } catch (error) {
            console.error("Google Calendar sync error:", error);
            showToast('error', 'Error', `Failed to sync with Google Calendar: ${error.response?.data?.error || error.message}`);
        }
    };

    const connectGoogleCalendar = async () => {
        try {
            showToast('info', 'Processing', 'Connecting to Google Calendar...');

            // This still uses the frontend authorizeGoogleCalendar function
            // because it handles the OAuth popup flow
            const token = await authorizeGoogleCalendar();

            // Use the new connect-google endpoint to sync existing events
            const response = await axios.post('https://api.frazza.online/calendar/connect-google', {
                userId: currentUser.uid,
                token: token
            });

            if (response.data.success) {
                setGoogleCalendarEnabled(true);
                showToast('success', 'Success', 'Google Calendar connected and synced successfully');
                fetchEvents(); // Refresh events after connection
            } else {
                throw new Error(response.data.error || "Connection failed");
            }
        } catch (error) {
            console.error("Google Calendar connection error:", error);
            showToast('error', 'Error', `Failed to connect Google Calendar: ${error.response?.data?.error || error.message}`);
        }
    };

    return (
        <>
            {googleCalendarEnabled ? (
                <div className="flex space-x-2">
                    <Button
                        label="Sync Calendar"
                        icon="pi pi-sync"
                        className="p-button-rounded p-button-success"
                        onClick={syncGoogleCalendar}
                    />
                    <Button
                        label="Disconnect Google Calendar"
                        icon="pi pi-google"
                        className="p-button-rounded p-button-danger"
                        onClick={() => setDisconnectDialog(true)}
                    />
                </div>
            ) : (
                <Button
                    label="Connect Google Calendar"
                    className="p-button-rounded p-button-secondary"
                    onClick={connectGoogleCalendar}
                />
            )}

            <DisconnectDialog
                visible={disconnectDialog}
                onHide={() => setDisconnectDialog(false)}
                onDisconnect={disconnectGoogleCalendar}
            />
        </>
    );
};

export default GoogleCalendarIntegration;